import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { VERSION } from '../version';
import './PortalCliente.css';

const API_BASE = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

function PortalCliente() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('resumen');
  const [sectionData, setSectionData] = useState(null);
  const [sectionLoading, setSectionLoading] = useState(false);

  useEffect(() => {
    if (!token) { setError('Token no proporcionado'); setLoading(false); return; }
    fetch(`${API_BASE}/portal_cliente.php?token=${encodeURIComponent(token)}`)
      .then(r => { if (!r.ok) throw new Error(r.status === 404 ? 'Portal no disponible' : 'Error'); return r.json(); })
      .then(data => { if (data.error) throw new Error(data.error); setInfo(data); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!info) return;
    if (info.coach_favicon) {
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = '/' + info.coach_favicon;
      document.head.appendChild(link);
    }
    const titulo = info.coach_empresa || info.coach || 'Portal del Cliente';
    document.title = `Portal — ${titulo}`;
  }, [info]);

  const loadSection = useCallback((section) => {
    if (!token) return;
    setSectionLoading(true);
    setSectionData(null);
    fetch(`${API_BASE}/portal_cliente.php?token=${encodeURIComponent(token)}&section=${section}`)
      .then(r => r.json())
      .then(data => setSectionData(data.data || []))
      .catch(() => setSectionData([]))
      .finally(() => setSectionLoading(false));
  }, [token]);

  useEffect(() => {
    if (!info) return;
    if (tab === 'citas') loadSection('citas');
    else if (tab === 'documentos') loadSection('documentos');
    else if (tab === 'facturas') loadSection('facturas');
    else if (tab === 'archivos') loadSection('archivos');
    else setSectionData(null);
  }, [tab, info, loadSection]);

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return d; }
  };
  const formatDateTime = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; }
  };
  const formatSize = (bytes) => {
    if (!bytes) return '-';
    const kb = bytes / 1024;
    return kb > 1024 ? (kb / 1024).toFixed(1) + ' MB' : kb.toFixed(0) + ' KB';
  };

  const estadoBadge = (estado) => {
    const map = {
      completada: 'badge-success', confirmada: 'badge-info', cancelada: 'badge-danger', pendiente: 'badge-warning',
      pagada: 'badge-success', enviada: 'badge-info', borrador: 'badge-secondary',
    };
    return map[estado] || 'badge-secondary';
  };

  const isVencida = (fechaVenc) => {
    if (!fechaVenc) return false;
    return new Date(fechaVenc) < new Date();
  };

  if (loading) return <div className="portal-loading"><div className="portal-spinner" /><p>Cargando portal...</p></div>;
  if (error) return <div className="portal-error"><h2>Portal del Cliente</h2><p>{error}</p></div>;
  if (!info) return null;

  const secciones = info.secciones || [];
  const navbarBg = info.navbar_bg || '#2c3e50';
  const navbarText = info.navbar_text || '#ffffff';
  const headerTitle = info.coach_empresa || info.coach || 'Portal del Cliente';

  return (
    <div className="portal-container">
      <header className="portal-header" style={{ background: navbarBg }}>
        {info.coach_logo && <img src={`/${info.coach_logo}`} alt="Logo" className="portal-logo" />}
        <div className="portal-header-info">
          <h1 style={{ color: navbarText }}>{headerTitle}</h1>
          <p className="portal-coach" style={{ color: navbarText }}>Portal del Cliente</p>
        </div>
        <button
          className="portal-exit-btn"
          style={{ color: navbarText, borderColor: navbarText }}
          onClick={() => {
            if (window.opener) { window.close(); }
            else { window.location.href = 'about:blank'; }
          }}
          title="Salir del portal"
        >
          Salir
        </button>
      </header>

      <div className="portal-welcome">
        <h2>Hola, {info.nombre} {info.apellidos}</h2>
        {info.proximas_citas && info.proximas_citas.length > 0 && (
          <div className="portal-next-apt">
            <strong>Próxima cita:</strong> {formatDateTime(info.proximas_citas[0].fecha_hora)} — {info.proximas_citas[0].titulo || 'Sesión'}
            {info.proximas_citas[0].sala_video_token && (() => {
              const hoyInicio = new Date(); hoyInicio.setHours(0,0,0,0);
              const fechaCita = new Date(info.proximas_citas[0].fecha_hora); fechaCita.setHours(0,0,0,0);
              const esPasada = fechaCita < hoyInicio;
              return esPasada ? (
                <span style={{ marginLeft: 12, display: 'inline-block', padding: '4px 12px', background: '#e9ecef', color: '#adb5bd', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'default' }}>
                  📹 Videollamada no disponible
                </span>
              ) : (
                <a
                  href={`/videollamada/${info.proximas_citas[0].sala_video_token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ marginLeft: 12, display: 'inline-block', padding: '4px 12px', background: '#7c3aed', color: '#fff', borderRadius: 6, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}
                >
                  📹 Unirse a videollamada
                </a>
              );
            })()}
          </div>
        )}
      </div>

      <nav className="portal-tabs">
        <button className={tab === 'resumen' ? 'active' : ''} onClick={() => setTab('resumen')}>Resumen</button>
        <button className={tab === 'citas' ? 'active' : ''} onClick={() => setTab('citas')}>Citas</button>
        {secciones.includes('documentos') && (
          <button className={tab === 'documentos' ? 'active' : ''} onClick={() => setTab('documentos')}>Documentos</button>
        )}
        {secciones.includes('facturas') && (
          <button className={tab === 'facturas' ? 'active' : ''} onClick={() => setTab('facturas')}>Facturas</button>
        )}
        {secciones.includes('archivos') && (
          <button className={tab === 'archivos' ? 'active' : ''} onClick={() => setTab('archivos')}>Archivos</button>
        )}
      </nav>

      <div className="portal-content">
        {tab === 'resumen' && (
          <div className="portal-resumen">
            <div className="portal-cards">
              <div className="portal-card" onClick={() => setTab('citas')}>
                <div className="portal-card-icon">📅</div>
                <h3>Citas</h3>
                <p>{info.proximas_citas?.length || 0} próximas</p>
              </div>
              {secciones.includes('documentos') && (
                <div className="portal-card" onClick={() => setTab('documentos')}>
                  <div className="portal-card-icon">📄</div>
                  <h3>Documentos</h3>
                  <p>{info.total_documentos || 0} documento{info.total_documentos !== 1 ? 's' : ''}</p>
                </div>
              )}
              {secciones.includes('facturas') && (
                <div className="portal-card" onClick={() => setTab('facturas')}>
                  <div className="portal-card-icon">💶</div>
                  <h3>Facturas</h3>
                  <p>{info.total_facturas || 0} factura{info.total_facturas !== 1 ? 's' : ''}</p>
                </div>
              )}
              {secciones.includes('archivos') && (
                <div className="portal-card" onClick={() => setTab('archivos')}>
                  <div className="portal-card-icon">📁</div>
                  <h3>Archivos</h3>
                  <p>{info.total_archivos || 0} archivo{info.total_archivos !== 1 ? 's' : ''}</p>
                </div>
              )}
            </div>

            {info.proximos_vencimientos && info.proximos_vencimientos.length > 0 && (
              <div className="portal-vencimientos">
                <h3>Próximos Vencimientos</h3>
                <div className="portal-table-wrap">
                  <table className="portal-table">
                    <thead><tr><th>Factura</th><th>Vencimiento</th><th>Importe</th><th>Estado</th></tr></thead>
                    <tbody>
                      {info.proximos_vencimientos.map(v => (
                        <tr key={v.id} className={isVencida(v.fecha_vencimiento) ? 'portal-row-vencida' : ''}>
                          <td><strong>{v.numero}</strong></td>
                          <td>
                            {formatDate(v.fecha_vencimiento)}
                            {isVencida(v.fecha_vencimiento) && <span className="portal-badge badge-danger" style={{ marginLeft: 8, fontSize: 10 }}>Vencida</span>}
                          </td>
                          <td>{Number(v.total || 0).toFixed(2)} {v.moneda || '€'}</td>
                          <td><span className={`portal-badge ${estadoBadge(v.estado)}`}>{v.estado}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'citas' && (
          <div className="portal-section">
            <h3>Mis Citas</h3>
            {sectionLoading ? <div className="portal-spinner-sm" /> : (
              sectionData && sectionData.length > 0 ? (
                <div className="portal-table-wrap">
                  <table className="portal-table">
                    <thead><tr><th>Fecha</th><th>Título</th><th>Duración</th><th>Modalidad</th><th>Estado</th><th></th></tr></thead>
                    <tbody>
                      {sectionData.map(c => (
                        <tr key={c.id}>
                          <td>{formatDateTime(c.fecha_hora)}</td>
                          <td>{c.titulo || 'Sesión'}</td>
                          <td>{c.duracion_minutos} min</td>
                          <td>{c.modalidad}</td>
                          <td><span className={`portal-badge ${estadoBadge(c.estado)}`}>{c.estado}</span></td>
                          <td>
                            {c.sala_video_token && (() => {
                              const hoyInicio = new Date(); hoyInicio.setHours(0,0,0,0);
                              const fechaCita = new Date(c.fecha_hora); fechaCita.setHours(0,0,0,0);
                              const esPasada = fechaCita < hoyInicio;
                              return esPasada ? (
                                <span style={{ padding: '4px 10px', background: '#e9ecef', color: '#adb5bd', borderRadius: 5, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'default' }}>
                                  📹 Finalizada
                                </span>
                              ) : (
                                <a
                                  href={`/videollamada/${c.sala_video_token}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ padding: '4px 10px', background: '#7c3aed', color: '#fff', borderRadius: 5, fontSize: 12, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}
                                >
                                  📹 Unirse
                                </a>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="portal-empty">No hay citas registradas</p>
            )}
          </div>
        )}

        {tab === 'documentos' && (
          <div className="portal-section">
            <h3>Mis Documentos</h3>
            {sectionLoading ? <div className="portal-spinner-sm" /> : (
              sectionData && sectionData.length > 0 ? (
                <div className="portal-table-wrap">
                  <table className="portal-table">
                    <thead><tr><th>Documento</th><th>Fecha</th><th>Acciones</th></tr></thead>
                    <tbody>
                      {sectionData.map(d => (
                        <tr key={d.id}>
                          <td>{d.nombre_archivo}</td>
                          <td>{formatDate(d.fecha_generacion)}</td>
                          <td>
                            {d.tiene_pdf && (
                              <a
                                href={`${API_BASE}/portal_cliente.php?token=${encodeURIComponent(token)}&section=documento_download&doc_id=${d.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="portal-btn portal-btn-sm"
                              >
                                Descargar PDF
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="portal-empty">No hay documentos disponibles</p>
            )}
          </div>
        )}

        {tab === 'facturas' && (
          <div className="portal-section">
            <h3>Mis Facturas</h3>
            {sectionLoading ? <div className="portal-spinner-sm" /> : (
              sectionData && sectionData.length > 0 ? (
                <div className="portal-table-wrap">
                  <table className="portal-table">
                    <thead><tr><th>Número</th><th>Fecha</th><th>Vencimiento</th><th>Total</th><th>Estado</th><th>Acciones</th></tr></thead>
                    <tbody>
                      {sectionData.map(f => (
                        <tr key={f.id}>
                          <td><strong>{f.numero}</strong></td>
                          <td>{formatDate(f.fecha_emision)}</td>
                          <td>{formatDate(f.fecha_vencimiento)}</td>
                          <td>{Number(f.total || 0).toFixed(2)} {f.moneda || '€'}</td>
                          <td><span className={`portal-badge ${estadoBadge(f.estado)}`}>{f.estado}</span></td>
                          <td>
                            <a
                              href={`${API_BASE}/portal_cliente.php?token=${encodeURIComponent(token)}&section=factura_pdf&factura_id=${f.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="portal-btn portal-btn-sm"
                            >
                              Ver PDF
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="portal-empty">No hay facturas disponibles</p>
            )}
          </div>
        )}

        {tab === 'archivos' && (
          <div className="portal-section">
            <h3>Mis Archivos</h3>
            {sectionLoading ? <div className="portal-spinner-sm" /> : (
              sectionData && sectionData.length > 0 ? (
                <div className="portal-table-wrap">
                  <table className="portal-table">
                    <thead><tr><th>Archivo</th><th>Categoría</th><th>Tamaño</th><th>Fecha</th><th>Acciones</th></tr></thead>
                    <tbody>
                      {sectionData.map(a => (
                        <tr key={a.id}>
                          <td>{a.nombre_original}</td>
                          <td><span className="portal-badge badge-secondary">{a.categoria}</span></td>
                          <td>{formatSize(a.tamano)}</td>
                          <td>{formatDate(a.fecha_subida)}</td>
                          <td>
                            <a
                              href={`${API_BASE}/portal_cliente.php?token=${encodeURIComponent(token)}&section=archivo_download&archivo_id=${a.id}`}
                              className="portal-btn portal-btn-sm"
                            >
                              Descargar
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="portal-empty">No hay archivos disponibles</p>
            )}
          </div>
        )}
      </div>

      <footer className="portal-footer" style={{ backgroundColor: navbarBg, color: navbarText }}>
        <p>
          &copy; 2025-26 CdL. All rights reserved. | Made with Love in Spain | v{VERSION}
          <span className="portal-footer-links">
            {' | '}<a href="/privacidad" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', opacity: 0.8 }}>Política de Privacidad</a>
            {' | '}<a href="/terminos" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', opacity: 0.8 }}>Términos y Condiciones</a>
          </span>
        </p>
      </footer>
    </div>
  );
}

export default PortalCliente;
